import React, { useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon, XCircleIcon, InformationCircleIcon, ChevronDownIcon, TrashIcon,
} from '@heroicons/react/24/outline';

const LEVEL = {
  error: { Icon: XCircleIcon, tone: 'text-red-700' },
  warning: { Icon: ExclamationTriangleIcon, tone: 'text-amber-700' },
  info: { Icon: InformationCircleIcon, tone: 'text-slate-500' },
};

export const Issue = ({ issue }) => {
  const { Icon, tone } = LEVEL[issue.level] || LEVEL.info;
  return (
    <div className={`flex items-start gap-1.5 text-xs ${tone}`}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{issue.message}</span>
    </div>
  );
};

export const rands = (n) => (n == null ? '—' : `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const units = (n) => Number(n).toLocaleString('en-ZA', { maximumFractionDigits: 2 });

const KIND_LABEL = {
  surgical: 'Procedure',
  anaesthetic_procedure: 'Anaesthetic proc.',
  consultation: 'Consultation',
  time: 'Time',
  modifier: 'Modifier',
};
const PROCEDURE_KINDS = ['surgical', 'anaesthetic_procedure', 'consultation'];
const PLACEHOLDER = /^Placeholder tariffs/;

/**
 * TariffLines — priced tariff lines with totals. Used by the preview invoice
 * (editable while it is a preview) and the fee calculator (read-only).
 *   result: { lines, issues, subtotal, vat_rate, vat_amount, total }
 *   onSwap(line_no, code) / onRemove(line_no): omit for read-only
 *   diagnoses: the invoice's coded ICD-10 lines — when given, each procedure line shows
 *   the codes it is claimed under (line.diagnoses); onSetDiagnoses(line_no, codes | null)
 *   lets them be changed (null = all of the invoice's codes).
 */
const TariffLines = ({ result, busy = false, onSwap, onRemove, diagnoses, onSetDiagnoses }) => {
  const [open, setOpen] = useState(null);
  const [dxOpen, setDxOpen] = useState(null);
  const placeholder = result.issues.find((i) => PLACEHOLDER.test(i.message));
  const issues = result.issues.filter((i) => i !== placeholder);

  return (
    <div className={busy ? 'pointer-events-none opacity-60' : ''}>
      {placeholder && (
        <div className={`mx-4 mb-2 flex gap-2 rounded-lg border px-3 py-2 text-xs ${placeholder.level === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span><strong>Placeholder tariffs.</strong> These codes, units and rules are not SAMA’s — amounts are for testing only.</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="w-28 px-4 py-1.5 font-semibold">Tariff</th>
              <th className="px-2 py-1.5 font-semibold">Item</th>
              <th className="w-16 px-2 py-1.5 text-right font-semibold">Units</th>
              <th className="w-24 px-2 py-1.5 text-right font-semibold">Rate</th>
              <th className="w-28 px-4 py-1.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {result.lines.map((l) => {
              const child = !PROCEDURE_KINDS.includes(l.kind);
              const alternatives = l.alternatives || [];
              return (
                <tr key={l.line_no} className="align-top">
                  <td className={`py-2 pr-2 ${child ? 'pl-8' : 'pl-4'}`}>
                    <div className={`font-mono font-bold ${l.code ? 'text-brand-navy' : 'text-red-600'}`}>{l.code || '—'}</div>
                    <div className="text-[10px] font-semibold uppercase text-slate-400">{KIND_LABEL[l.kind] || l.kind}</div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className={child ? 'text-slate-600' : 'text-slate-800'}>{l.description || <span className="text-red-600">No matching code</span>}</div>
                        {l.said && <div className="text-xs text-slate-400">You said: “{l.said}”</div>}
                        {l.basis && <div className="text-xs text-slate-500">{l.basis}</div>}
                        {l.reason && <div className="text-xs text-slate-400">{l.reason}</div>}
                      </div>
                      {l.confidence && (
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${l.confidence === 'high' ? 'bg-green-100 text-green-700' : l.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {l.confidence}
                        </span>
                      )}
                      {onRemove && !child && (
                        <button onClick={() => onRemove(l.line_no)} title="Remove this line" className="shrink-0 rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-600">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {diagnoses && !child && Array.isArray(l.diagnoses) && (
                      <LineDiagnoses
                        line={l}
                        diagnoses={diagnoses}
                        editing={dxOpen === l.line_no}
                        onEdit={onSetDiagnoses ? () => setDxOpen(dxOpen === l.line_no ? null : l.line_no) : undefined}
                        onSave={(codes) => { setDxOpen(null); onSetDiagnoses(l.line_no, codes); }}
                      />
                    )}
                    {l.issues?.length > 0 && <div className="mt-1 space-y-0.5">{l.issues.map((i) => <Issue key={i.message} issue={i} />)}</div>}
                    {alternatives.length > 0 && (
                      <button onClick={() => setOpen(open === l.line_no ? null : l.line_no)} className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-brand-teal hover:underline">
                        {alternatives.length} alternative{alternatives.length === 1 ? '' : 's'}
                        <ChevronDownIcon className={`h-3 w-3 transition ${open === l.line_no ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {open === l.line_no && (
                      <ul className="mt-1 space-y-0.5 rounded-md bg-slate-50 px-2 py-1.5">
                        {alternatives.map((a) => (
                          <li key={a.code} className="flex items-center gap-2 text-xs text-slate-600">
                            <span className="font-mono font-semibold text-brand-navy">{a.code}</span>
                            <span className="flex-1">{a.description}</span>
                            {onSwap && (
                              <button onClick={() => { setOpen(null); onSwap(l.line_no, a.code); }} className="rounded px-1.5 py-0.5 font-semibold text-brand-teal hover:bg-teal-50">
                                Use
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-700">{units(l.units)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-500">{l.unit_rate == null ? '—' : rands(l.unit_rate)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right font-semibold tabular-nums text-slate-800">{rands(l.amount)}</td>
                </tr>
              );
            })}
            {result.lines.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-xs text-slate-400">No tariff lines.</td></tr>
            )}
          </tbody>
          <tfoot className="border-t border-slate-200 text-sm">
            <tr>
              <td colSpan={4} className="px-2 pt-2 text-right text-slate-500">Subtotal</td>
              <td className="px-4 pt-2 text-right tabular-nums text-slate-700">{rands(result.subtotal)}</td>
            </tr>
            {result.vat_rate > 0 && (
              <tr>
                <td colSpan={4} className="px-2 text-right text-slate-500">VAT {Math.round(result.vat_rate * 100)}%</td>
                <td className="px-4 text-right tabular-nums text-slate-700">{rands(result.vat_amount)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={4} className="px-2 pb-2 text-right font-bold text-brand-deep">Total</td>
              <td className="whitespace-nowrap px-4 pb-2 text-right text-base font-bold tabular-nums text-brand-deep">{rands(result.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {issues.length > 0 && <div className="space-y-1 px-4 pb-2">{issues.map((i) => <Issue key={i.message} issue={i} />)}</div>}
    </div>
  );
};

// The ICD-10 codes a procedure line is claimed under, with a tick-list to change them.
// Codes always go in the invoice's order, so the primary leads wherever it is ticked.
export const LineDiagnoses = ({ line, diagnoses, editing, onEdit, onSave }) => {
  const [picked, setPicked] = useState(line.diagnoses);
  useEffect(() => { if (editing) setPicked(line.diagnoses); }, [editing, line.diagnoses]);
  const toggle = (code) => setPicked((p) => (p.includes(code) ? p.filter((c) => c !== code) : [...p, code]));
  const all = picked.length === diagnoses.length;

  return (
    <div className="mt-1">
      <div className="flex flex-wrap items-center gap-1 text-[11px]">
        <span className="font-semibold text-slate-500">ICD-10</span>
        {line.diagnoses.map((c, i) => (
          <span key={c} className={`rounded px-1.5 py-0.5 font-mono font-semibold ${i === 0 ? 'bg-brand-navy/10 text-brand-navy' : 'bg-slate-100 text-slate-600'}`}>{c}</span>
        ))}
        {!line.diagnoses.length && <span className="font-semibold text-red-600">none</span>}
        {line.icd10_all && diagnoses.length > 1 && <span className="text-slate-400">(all)</span>}
        {onEdit && diagnoses.length > 0 && <button onClick={onEdit} className="font-semibold text-brand-teal hover:underline">{editing ? 'Close' : 'Change'}</button>}
      </div>
      {editing && (
        <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
          <p className="mb-1 text-[11px] text-slate-500">Which diagnoses is this line claimed under? They go on the claim in the invoice's order.</p>
          {diagnoses.map((d) => (
            <label key={d.code} className="flex items-start gap-2 py-0.5 text-xs text-slate-700">
              <input type="checkbox" checked={picked.includes(d.code)} onChange={() => toggle(d.code)} className="mt-0.5 rounded border-slate-300" />
              <span className="font-mono font-semibold text-brand-navy">{d.code}</span>
              <span className="flex-1">{d.description}{d.is_primary ? <span className="ml-1 text-[10px] font-bold uppercase text-slate-400">primary</span> : null}</span>
            </label>
          ))}
          <div className="mt-1 flex gap-2">
            <button onClick={() => onSave(all ? null : diagnoses.map((d) => d.code).filter((c) => picked.includes(c)))} disabled={!picked.length} className="rounded-md bg-brand-navy px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-40">Save</button>
            {!all && <button onClick={() => setPicked(diagnoses.map((d) => d.code))} className="text-[11px] font-semibold text-slate-500">Tick all</button>}
          </div>
        </div>
      )}
    </div>
  );
};

export default TariffLines;
